import React, { useState, useEffect, useRef } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { requestCode, verifyCode, setPassword, login } from '../lib/api'
import { RoutesEnum } from '../lib/routes'
import ModTextInputMinimal from '../ui/ModTextInputMinimal/ModTextInputMinimal'
import ModTabs from '../ui/ModTabs/ModTabs'
import ModTabPanels from '../ui/ModTabs/ModTabPanels'
import ModTabPanel from '../ui/ModTabs/ModTabPanel'
import { useLang } from '../lib/useLang'
import './Auth.css'

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export default function Auth() {
  const navigate = useNavigate()
  const { t } = useLang()

  // Registration is intentionally hidden from the UI for now — login into existing
  // accounts only. Flip this to true to restore the full sign-up flow; all sign-up
  // state, handlers, and JSX below are kept intact so it can be brought back easily.
  const REGISTRATION_ENABLED = false

  const tabs = REGISTRATION_ENABLED
    ? [
        { id: 'sign-in', label: t('auth.signIn') },
        { id: 'sign-up', label: t('auth.signUp') },
      ]
    : [{ id: 'sign-in', label: t('auth.signIn') }]

  const [activeTab, setActiveTab] = useState('sign-in')
  const [regStep, setRegStep] = useState(1)
  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [pwd, setPwd] = useState('')
  const [pwd2, setPwd2] = useState('')
  const [agreeConsent, setAgreeConsent] = useState(false)
  const [marketingConsent, setMarketingConsent] = useState(false)
  const [loading, setLoading] = useState(false)
  const [msg, setMsg] = useState('')
  const [err, setErr] = useState('')
  const containerRef = useRef(null)
  const cardRef = useRef(null)
  const [mousePosition, setMousePosition] = useState({ x: 0.5, y: 0.5 })
  const [cardTilt, setCardTilt] = useState({ x: 0, y: 0 })

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (containerRef.current && cardRef.current) {
        const containerRect = containerRef.current.getBoundingClientRect()
        const cardRect = cardRef.current.getBoundingClientRect()

        const containerX = (e.clientX - containerRect.left) / containerRect.width
        const containerY = (e.clientY - containerRect.top) / containerRect.height
        setMousePosition({ x: containerX, y: containerY })

        // 3D tilt effect
        const cardX = (e.clientX - cardRect.left) / cardRect.width
        const cardY = (e.clientY - cardRect.top) / cardRect.height
        const tiltX = (cardY - 0.5) * 10
        const tiltY = (0.5 - cardX) * 10
        setCardTilt({ x: tiltX, y: tiltY })
      }
    }

    const container = containerRef.current
    if (container) {
      container.addEventListener('mousemove', handleMouseMove)
      return () => container.removeEventListener('mousemove', handleMouseMove)
    }
  }, [])

  const resetState = () => {
    setRegStep(1)
    setCode('')
    setPwd('')
    setPwd2('')
    setAgreeConsent(false)
    setMarketingConsent(false)
    setMsg('')
    setErr('')
  }

  const handleTabChange = tabId => {
    setActiveTab(tabId)
    resetState()
  }

  const onSendCode = async () => {
    setErr('')
    setMsg('')
    if (!emailRegex.test(email)) {
      setErr(t('auth.errValidEmail'))
      return
    }
    if (!agreeConsent) {
      setErr(t('auth.errConsentRequired'))
      return
    }
    setLoading(true)
    try {
      const response = await requestCode(email)
      if (response?.code) {
        setMsg(t('auth.devCodeSent', { code: response.code }))
      } else {
        setMsg(t('auth.msgCodeSent'))
      }
      setRegStep(2)
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || t('auth.failedSendCode'))
    } finally {
      setLoading(false)
    }
  }

  const onVerifyCode = async () => {
    setErr('')
    setMsg('')
    if (!/^[0-9]{6}$/.test(code)) {
      setErr(t('auth.errCode6'))
      return
    }
    setLoading(true)
    try {
      await verifyCode(email, code)
      setMsg(t('auth.msgCodeVerified'))
      setRegStep(3)
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || t('auth.invalidCode'))
    } finally {
      setLoading(false)
    }
  }

  const onSetPassword = async () => {
    setErr('')
    setMsg('')
    if (pwd.length < 8) {
      setErr(t('auth.errPwdMin8'))
      return
    }
    if (pwd !== pwd2) {
      setErr(t('auth.errPwdMatch'))
      return
    }
    setLoading(true)
    try {
      await setPassword(email, code, pwd, marketingConsent)
      setMsg(t('auth.msgPasswordSet'))
      resetState()
      setActiveTab('sign-in')
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || t('auth.failedSetPassword'))
    } finally {
      setLoading(false)
    }
  }

  const [loginPwd, setLoginPwd] = useState('')
  const [loginLoading, setLoginLoading] = useState(false)
  const onLogin = async () => {
    setErr('')
    setMsg('')
    if (!emailRegex.test(email)) {
      setErr(t('auth.errValidEmail'))
      return
    }
    if (!loginPwd) {
      setErr(t('auth.errEnterPassword'))
      return
    }
    setLoginLoading(true)
    try {
      await login(email, loginPwd)
      setMsg(t('auth.msgSignedIn'))
      navigate('/app')
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || t('auth.failedSignIn'))
    } finally {
      setLoginLoading(false)
    }
  }

  const gradientStyle = {
    background: `radial-gradient(circle at ${mousePosition.x * 100}% ${mousePosition.y * 100}%,
      rgba(255, 90, 31, 0.10) 0%,
      rgba(255, 90, 31, 0.04) 30%,
      rgba(245, 241, 232, 1) 65%)`,
  }

  const cardStyle = {
    transform: `perspective(1000px) rotateX(${cardTilt.x}deg) rotateY(${cardTilt.y}deg)`,
    transformStyle: 'preserve-3d',
  }

  const signInForm = (
    <form className='flex flex-col gap-8' onSubmit={e => { e.preventDefault(); onLogin() }}>
      <ModTextInputMinimal
        value={email}
        onChange={e => setEmail(e.target.value)}
        placeholder={t('auth.emailPlaceholder')}
        autoComplete='new-email'
        type='text'
        inputMode='email'
        name='email-signin'
        data-form-type='other'
        required
      />
      <ModTextInputMinimal
        label={t('auth.password')}
        type='password'
        value={loginPwd}
        onChange={e => setLoginPwd(e.target.value)}
        placeholder={t('auth.passwordPlaceholder')}
        autoComplete='new-password'
        name='password-signin'
        data-form-type='other'
        required
      />

      <button
        type='submit'
        disabled={loginLoading}
        className='header-button-primary mt-4 w-full flex items-center justify-center gap-2'
      >
        {loginLoading ? (
          <>
            <span className='loading loading-spinner loading-sm'></span>
            {t('auth.signingIn')}
          </>
        ) : (
          t('auth.signIn')
        )}
      </button>
    </form>
  )

  return (
    <div
      ref={containerRef}
      className='auth-container relative flex min-h-screen items-center justify-center overflow-hidden'
      style={gradientStyle}
    >
      {/* Soft accent orbs (cream theme) */}
      <div className='absolute top-1/4 left-1/4 h-[520px] w-[520px] -translate-x-1/2 -translate-y-1/2 rounded-full' style={{ background: 'radial-gradient(circle, rgba(255, 90, 31, 0.10), transparent 70%)', filter: 'blur(40px)' }} />
      <div className='absolute bottom-1/4 right-1/4 h-[520px] w-[520px] translate-x-1/2 translate-y-1/2 rounded-full' style={{ background: 'radial-gradient(circle, rgba(14, 14, 16, 0.08), transparent 70%)', filter: 'blur(40px)' }} />

      {/* Grid pattern overlay */}
      <div className='absolute inset-0 bg-grid-pattern opacity-[0.03]' />

      <div className='relative z-10 w-full max-w-lg px-4 sm:px-6'>
        <div
          ref={cardRef}
          className='auth-card animate-scale-in'
          style={cardStyle}
        >
          <div className='premium-card'>
            {/* Animated border gradient */}
            <div className='premium-card-border' />

            {/* Main content */}
            <div className='premium-card-content'>
              <div className='mb-10 text-center'>
                <h1 className='mb-3 text-4xl font-light tracking-tight'>{t('auth.welcome')}</h1>
                <p className='text-sm font-light opacity-60'>{t('auth.subtitle')}</p>
              </div>

              {REGISTRATION_ENABLED ? (
                <ModTabs tabs={tabs} value={activeTab} onChange={handleTabChange} className='mb-8'>
                <ModTabPanels>
                  <ModTabPanel value='sign-in'>
                    {signInForm}
                  </ModTabPanel>

                  <ModTabPanel value='sign-up'>
                    <div className='flex flex-col gap-6'>
                      {regStep === 1 && (
                        <form className='flex flex-col gap-6' onSubmit={e => { e.preventDefault(); onSendCode() }}>
                          <ModTextInputMinimal
                            value={email}
                            onChange={e => setEmail(e.target.value)}
                            placeholder={t('auth.emailPlaceholder')}
                            autoComplete='new-email'
                            type='text'
                            inputMode='email'
                            name='email-signup'
                            data-form-type='other'
                            required
                          />
                          <label className='flex items-start gap-2 text-left text-[0.78rem] leading-[1.5] text-ink/70'>
                            <input
                              type='checkbox'
                              className='mt-0.5 h-4 w-4 shrink-0 accent-[#FF5A1F]'
                              checked={agreeConsent}
                              onChange={e => setAgreeConsent(e.target.checked)}
                            />
                            <span>
                              {t('auth.consentPrefix')}
                              <Link to={RoutesEnum.TERMS} target='_blank' rel='noreferrer' className='underline hover:text-ink'>{t('auth.consentTerms')}</Link>
                              {t('auth.consentComma')}
                              <Link to={RoutesEnum.PRIVACY} target='_blank' rel='noreferrer' className='underline hover:text-ink'>{t('auth.consentPrivacy')}</Link>
                              {t('auth.consentAnd')}
                              <Link to={RoutesEnum.CONSENT} target='_blank' rel='noreferrer' className='underline hover:text-ink'>{t('auth.consentPdn')}</Link>.
                            </span>
                          </label>
                          <button
                            type='submit'
                            disabled={loading || !agreeConsent}
                            className='header-button-primary mt-2 w-full flex items-center justify-center gap-2'
                          >
                            {loading ? (
                              <>
                                <span className='loading loading-spinner loading-sm'></span>
                                {t('auth.sending')}
                              </>
                            ) : (
                              t('auth.sendCode')
                            )}
                          </button>
                        </form>
                      )}
                      {regStep === 2 && (
                        <form className='flex flex-col gap-6' onSubmit={e => { e.preventDefault(); onVerifyCode() }}>
                          <ModTextInputMinimal
                            label={t('auth.verificationCode')}
                            value={code}
                            onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
                            placeholder='000000'
                            maxLength={6}
                            required
                          />
                          <div className='flex gap-3'>
                            <button type='button' onClick={() => setRegStep(1)} className='header-button-secondary flex-1'>
                              {t('auth.back')}
                            </button>
                            <button
                              type='submit'
                              disabled={loading}
                              className='header-button-primary flex-1 flex items-center justify-center gap-2'
                            >
                              {loading ? (
                                <>
                                  <span className='loading loading-spinner loading-sm'></span>
                                  {t('auth.verifying')}
                                </>
                              ) : (
                                t('auth.verify')
                              )}
                            </button>
                          </div>
                        </form>
                      )}
                      {regStep === 3 && (
                        <form className='flex flex-col gap-6' onSubmit={e => { e.preventDefault(); onSetPassword() }}>
                          <ModTextInputMinimal
                            label={t('auth.password')}
                            type='password'
                            value={pwd}
                            onChange={e => setPwd(e.target.value)}
                            placeholder={t('auth.atLeast8')}
                            autoComplete='new-password'
                            name='password-new'
                            data-form-type='other'
                            required
                          />
                          <ModTextInputMinimal
                            label={t('auth.confirmPassword')}
                            type='password'
                            value={pwd2}
                            onChange={e => setPwd2(e.target.value)}
                            placeholder={t('auth.repeatPassword')}
                            autoComplete='new-password'
                            name='password-confirm'
                            data-form-type='other'
                            required
                          />
                          <label className='flex items-start gap-2 text-left text-[0.78rem] leading-[1.5] text-ink/70'>
                            <input
                              type='checkbox'
                              className='mt-0.5 h-4 w-4 shrink-0 accent-[#FF5A1F]'
                              checked={marketingConsent}
                              onChange={e => setMarketingConsent(e.target.checked)}
                            />
                            <span>{t('auth.marketingConsent')}</span>
                          </label>
                          <div className='flex gap-3'>
                            <button type='button' onClick={() => setRegStep(2)} className='header-button-secondary flex-1'>
                              {t('auth.back')}
                            </button>
                            <button
                              type='submit'
                              disabled={loading}
                              className='header-button-primary flex-1 flex items-center justify-center gap-2'
                            >
                              {loading ? (
                                <>
                                  <span className='loading loading-spinner loading-sm'></span>
                                  {t('auth.creating')}
                                </>
                              ) : (
                                t('auth.createAccount')
                              )}
                            </button>
                          </div>
                        </form>
                      )}
                    </div>
                  </ModTabPanel>
                </ModTabPanels>
                </ModTabs>
              ) : (
                <div className='mb-8'>{signInForm}</div>
              )}

              {(msg || err) && (
                <div
                  className={`premium-message animate-slide-in-up ${
                    err ? 'premium-message-error' : 'premium-message-success'
                  }`}
                >
                  <div className='premium-message-content'>{err || msg}</div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
